import type { TFunction } from 'i18next';

import type { ReportTableResponse } from '../../api/types';

/**
 * Turning a report's typed cells into something a person reads.
 *
 * The API deliberately sends no headings and no formatted numbers — columns carry a key and a type,
 * money is an integer in piastres, and the currency is stated once (NFR-006). Everything a reader
 * sees is decided here, in the language they chose, which is the only way an Arabic report can be
 * an actual translation rather than a second implementation on the server.
 */

export type ColumnType = NonNullable<
  NonNullable<ReportTableResponse['columns']>[number]['type']
>;

/**
 * A column's heading, in the reader's language.
 *
 * Looked up per report first, then falling back to a shared key: `label` means "Branch" in REP-02
 * and "Package" in REP-03, while `net` means the same thing everywhere. The fallback is what stops
 * the message catalogue needing eight copies of every money column.
 */
export function reportHeading(t: TFunction, reportKey: string, columnKey: string): string {
  /* An explicit empty default rather than comparing the result against the key it was looked up
     by. That comparison was the idiom until i18next was given a handler for missing keys — which
     humanises them, so `reports.columns.income.gross` started coming back as "Gross" instead of as
     itself, the comparison stopped matching, and every report column silently skipped the shared
     Arabic heading underneath it in favour of an English word. A default is what i18next offers
     for exactly this, and it does not care what the missing-key handler does. */
  const specific = t(`reports.columns.${reportKey}.${columnKey}`, { defaultValue: '' });
  return specific || t(`reports.columns.common.${columnKey}`);
}

/**
 * One cell.
 *
 * Money arrives in minor units and is divided here, once, by a hundred — never on the server and
 * never twice. Numbers are formatted with the reader's locale but always in Western digits
 * (ASM-004): an Arabic UI in Egypt shows 1,240 rather than ١٬٢٤٠, which is what the partner clinics
 * actually write on paper.
 */
export function formatCell(
  value: unknown,
  type: ColumnType | undefined,
  currency: string | undefined,
): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  switch (type) {
    case 'MONEY':
      // Currency in front, matching `catalog/money.ts`. A trailing code repeated down forty rows
      // of a report says nothing forty times and pushes the digits out of alignment.
      return `${currency ?? 'EGP'} ${decimal(Number(value) / 100, 2)}`;
    case 'PERCENT':
      return `${decimal(Number(value), 1)}%`;
    case 'HOURS':
    case 'DAYS':
      return decimal(Number(value), 1);
    case 'INTEGER':
      return decimal(Number(value), 0);
    default:
      return String(value);
  }
}

/** Right-aligned in an LTR table and left-aligned in RTL: numbers line up on their units either way. */
export function isNumeric(type: ColumnType | undefined): boolean {
  return type !== 'TEXT' && type !== 'DATE' && type !== undefined;
}

function decimal(value: number, places: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-EG', {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(value);
}

export interface ExportFilters {
  readonly from: string;
  readonly to: string;
  readonly dimension: string | null;
  readonly branchId: string | null;
}

/**
 * The export path, with the headings the reader is looking at attached.
 *
 * The server writes whatever it is given into the header row. Sending the on-screen headings is
 * what makes the file match the table it came from — including in Arabic, where the alternative
 * would be a spreadsheet whose columns are named in a language its reader did not pick.
 */
export function exportPath(
  clinicId: string,
  table: ReportTableResponse,
  filters: ExportFilters,
  t: TFunction,
): string {
  const parameters = new URLSearchParams({ from: filters.from, to: filters.to });
  if (filters.dimension) {
    parameters.set('dimension', filters.dimension);
  }
  if (filters.branchId) {
    parameters.set('branchId', filters.branchId);
  }
  (table.columns ?? []).forEach((column) => {
    parameters.append('headings', reportHeading(t, table.key ?? '', column.key ?? ''));
  });
  parameters.append('headings', t('reports.columns.common.total'));
  return `/clinics/${clinicId}/reports/${table.key}/export?${parameters.toString()}`;
}

/** `laseera-income-2026-03-01-2026-03-31.csv` — sortable, and says what it is. */
export function exportFilename(table: ReportTableResponse, filters: ExportFilters): string {
  return `laseera-${table.key}-${filters.from}-${filters.to}.csv`;
}
